import React, { useEffect, useMemo, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, Spinner } from '@fluentui/react';
import { getCrmEntityState, getMarketEntity, pullCrmEntity, refreshMarketEntityChildren } from '../api/prospectingApi';
import CompetitionControl from './CompetitionControl';
import EntityEvidencePanel, { PipedriveEntityLinks } from './EntityEvidencePanel';
import { formatMoney } from '../utils/metrics';
import MaluchEntitySource from './MaluchEntitySource';

function Value({ label, children }) {
  return <div className="prospecting-detail-value"><span>{label}</span><strong>{children === null || children === undefined || children === '' ? '—' : children}</strong></div>;
}
function todayLocal() {
  try { return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); }
  catch (error) { return new Date().toISOString().slice(0, 10); }
}

export default function EntityPanel({ token, product, entityId, onDismiss, onSyncRequest, onChanged, onOpenEntity }) {
  const [item, setItem] = useState(null);
  const [crm, setCrm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshingChildren, setRefreshingChildren] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [pulling, setPulling] = useState(false);

  function load(id = entityId) {
    if (!id) return Promise.resolve();
    return Promise.all([
      getMarketEntity(token, { product, id }),
      getCrmEntityState(token, { product, id }).catch(() => null),
    ]).then(([response, crmState]) => { setItem(response?.item || null); setCrm(crmState); });
  }

  useEffect(() => {
    if (!entityId) { setItem(null); setCrm(null); setRefreshResult(null); return undefined; }
    const controller = new AbortController();
    setLoading(true); setError(null); setRefreshResult(null);
    Promise.all([
      getMarketEntity(token, { product, id: entityId }, controller.signal),
      getCrmEntityState(token, { product, id: entityId }, controller.signal).catch(() => null),
    ]).then(([response, crmState]) => { setItem(response?.item || null); setCrm(crmState); })
      .catch((requestError) => { if (requestError.name !== 'AbortError') setError(requestError.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [token, product, entityId]);

  const source = String(item?.registry_source || '').toUpperCase();
  const canRefreshChildren = source === 'RSPO' || source === 'EMPATIA';
  const registryLabel = source === 'RSPO' ? 'Otwórz wpis w RSPO' : source === 'EMPATIA' ? 'Otwórz wpis w Empatii' : 'Otwórz źródło';
  const childrenFreshToday = useMemo(() => Boolean(item?.children_as_of) && item.children_as_of === todayLocal(), [item?.children_as_of]);
  const contacts = crm?.contacts || [];

  async function handleRefreshChildren() {
    if (!item?.id || refreshingChildren) return;
    setRefreshingChildren(true); setError(null); setRefreshResult(null);
    try {
      const response = await refreshMarketEntityChildren(token, { product, id: item.id });
      if (response?.item) setItem(response.item);
      setRefreshResult(response?.refresh || null);
      if (onChanged) onChanged();
    } catch (requestError) { setError(requestError?.message || 'Nie udało się odświeżyć liczby dzieci.'); }
    finally { setRefreshingChildren(false); }
  }

  async function handlePull() {
    if (!item?.id || pulling) return;
    setPulling(true); setError(null);
    try {
      const response = await pullCrmEntity(token, { id: item.id, product });
      if (response?.status === 'not_linked') {
        const diagnostics = response?.discovery?.diagnostics || {};
        const legacyFields = (diagnostics.legacy_rspo_fields || []).map((field) => field.name || field.code).filter(Boolean).join(', ');
        setError(`Nie znaleziono organizacji w Pipedrive dla ${item.registry_source || ''} ${item.registry_id || ''}.${diagnostics.organizations_indexed !== undefined ? ` Sprawdzono ${diagnostics.organizations_indexed} organizacji.` : ''}${legacyFields ? ` Pola RSPO: ${legacyFields}.` : ''}`);
        return;
      }
      await load(item.id);
      if (onChanged) onChanged();
    } catch (e) { setError(e.message); }
    finally { setPulling(false); }
  }

  return (
    <Panel isOpen={Boolean(entityId)} onDismiss={onDismiss} type={PanelType.medium} headerText={item?.name || 'Placówka'} closeButtonAriaLabel="Zamknij">
      {loading ? <Spinner label="Wczytuję kartę placówki…" /> : null}
      {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
      {item ? (
        <div className="prospecting-detail">
          {item.is_own_customer ? <MessageBar messageBarType={MessageBarType.success}>Nasza placówka — wyłączona z potencjalnego ARR/MRR, liczby celów, dzieci potencjału i średnich scoringu.</MessageBar> : null}
          {item.startup_priority?.priority && !item.is_own_customer ? (
            <MessageBar messageBarType={MessageBarType.success}>
              {item.startup_priority.status === 'new_registry_entry' ? (
                <>Nowa w ONE / nieobecna w historycznym CSV — priorytet 100/100 do {item.startup_priority.expires_at?.slice(0, 10)}. To sygnał prospectingowy, nie potwierdzona data otwarcia.</>
              ) : (
                <>Priorytet otwarcia — scoring 100/100. {item.startup_priority.status === 'preopening' ? 'Placówka przed otwarciem' : 'Nowo otwarta placówka'}
                  {item.startup_priority.opening_date ? ` · ${item.startup_priority.opening_date}` : ''}. Potwierdzona data startu.</>
              )}
            </MessageBar>
          ) : null}
          {item.startup_priority?.source_url && /^https:\/\//i.test(item.startup_priority.source_url) ? (<div className="prospecting-detail-note">{item.startup_priority.status === 'new_registry_entry' ? 'Historyczny CSV: ' : `Data otwarcia: ${item.startup_priority.opening_date || '—'} · `}<a href={item.startup_priority.source_url} target="_blank" rel="noopener noreferrer">{item.startup_priority.status === 'new_registry_entry' ? 'Zobacz źródło porównania ↗' : 'Zobacz oficjalne potwierdzenie ↗'}</a></div>) : null}
          {item.startup_priority?.status === 'suspended' ? (
            <MessageBar messageBarType={MessageBarType.warning}>Empatia: działalność zawieszona. Brak automatycznego bonusu za nowe otwarcie.</MessageBar>
          ) : null}
          <div className="prospecting-detail-score-grid">
            <Value label="Scoring">{item.potential_score === null ? '—' : `${item.potential_score}/100`}</Value>
            <Value label="Efektywny scoring">{item.is_own_customer ? 'wyłączony z rynku' : (item.effective_score === null ? '—' : `${item.effective_score}/100`)}</Value>
            <Value label="ARR">{item.is_own_customer ? 'wyłączony z potencjału' : (item.annual_value === null ? 'do uzupełnienia' : formatMoney(item.annual_value))}</Value>
          </div>

          <div className="prospecting-crm-primary-actions">
            <PrimaryButton
              text={item.is_own_customer ? 'Nasza placówka — bez prospectingu' : (item.potential_score === 0 ? 'Scoring 0 — nie dodajemy' : (item.crm_linked || item.pipedrive_org_id ? 'Synchronizuj z Pipedrive' : 'Dodaj do Pipedrive'))}
              disabled={item.is_own_customer || item.potential_score === 0}
              onClick={() => onSyncRequest?.({ selection_type: 'entity', entity_id: item.id, product, title: item.crm_linked || item.pipedrive_org_id ? `Synchronizuj: ${item.name}` : `Dodaj do Pipedrive: ${item.name}` })}
            />
            {item.crm_linked || item.pipedrive_org_id ? (
              <DefaultButton
                text={pulling ? 'Pobieram…' : 'Pobierz zmiany z Pipedrive'}
                disabled={pulling}
                onClick={handlePull}
              />
            ) : null}
          </div>

          <h3>Źródło rejestrowe</h3>
          <Value label="Rejestr">{`${item.registry_source || ''} ${item.registry_id || ''}`.trim()}</Value>
          <div className="prospecting-registry-actions">
            {item.registry_url ? <a className="prospecting-registry-link" href={item.registry_url} target="_blank" rel="noreferrer">{registryLabel} ↗</a> : null}
            {canRefreshChildren ? <button type="button" className="prospecting-refresh-children-btn" onClick={handleRefreshChildren} disabled={refreshingChildren}>{refreshingChildren ? 'Pobieram z rejestru…' : 'Odśwież liczbę dzieci'}</button> : null}
          </div>
          {canRefreshChildren ? <div className={`prospecting-children-freshness ${childrenFreshToday ? '' : 'is-stale'}`}>{childrenFreshToday ? `Liczba dzieci została potwierdzona dzisiaj (${item.children_as_of}).` : item.children_as_of ? `Liczba dzieci z dnia ${item.children_as_of}. Przy synchronizacji spróbujemy odświeżyć rejestr; jeśli będzie niedostępny, użyjemy tych danych z ONE.` : 'Brak daty potwierdzenia. Przy synchronizacji spróbujemy odświeżyć rejestr, ale jego awaria nie zablokuje Pipedrive.'}</div> : null}
          {refreshResult ? <div className="prospecting-refresh-result">Rejestr zwrócił {refreshResult.children_count} dzieci{refreshResult.previous_children_count !== null && refreshResult.previous_children_count !== refreshResult.children_count ? ` (wcześniej ${refreshResult.previous_children_count})` : ''}.</div> : null}

          <h3>Identyfikacja</h3>
          <Value label="Typ">{item.institution_type}</Value><Value label="Grupa rynku">{item.market_group}</Value><Value label="Status">{item.public_status}</Value><Value label="Typ prowadzącego (rejestr)">{item.operator_type || "nieustalony"}</Value>

          {source === 'MALUCH' ? <MessageBar messageBarType={MessageBarType.warning}>To projekt MALUCH+ z 2023 r., nie potwierdzona nowa budowa. Wartość ARR opiera się na przyznanych miejscach, nie na faktycznej frekwencji. Lokalizacja oznacza gminę, a nie adres obiektu.</MessageBar> : null}
          <h3>Placówka</h3>
          <Value label="Adres">{item.address}</Value><Value label="Liczba dzieci">{item.children_count}</Value><Value label="Stan liczby dzieci">{item.children_as_of}</Value><Value label="Pojemność">{item.capacity}</Value><Value label="Prowadzący">{item.operator_name}</Value><Value label="Telefon źródłowy">{item.phone}</Value><Value label="E-mail źródłowy">{item.email}</Value><Value label="WWW">{item.website}</Value>

          <MaluchEntitySource token={token} entityId={item.id} onChanged={onChanged} onOpenEntity={onOpenEntity} />

          <h3>Status rynku i konkurencja</h3>
          <CompetitionControl token={token} entityId={item.id} product={product} onChanged={async () => { await load(item.id); if (onChanged) onChanged(); }} />

          <EntityEvidencePanel token={token} entityId={item.id} onChanged={onChanged} />

          <h3>CRM</h3>
          <PipedriveEntityLinks crm={crm} />
          <Value label="Powiązanie Pipedrive">{item.crm_linked || item.pipedrive_org_id ? 'Tak' : 'Nie'}</Value>
          <Value label="Status prospectu">{item.prospect_status}</Value><Value label="Kolejny krok">{item.next_action_at}</Value><Value label="Pipedrive organization">{item.pipedrive_org_id}</Value><Value label="Pipedrive lead">{item.pipedrive_lead_id}</Value><Value label="Pipedrive deal">{item.pipedrive_deal_id}</Value>
          {contacts.length ? (
            <div className="prospecting-crm-contacts">
              <strong>Kontakty z Pipedrive</strong>
              {contacts.map((contact) => <div key={contact.pipedrive_person_id}><span>{contact.name}{contact.is_primary ? ' · główny' : ''}</span><small>{contact.phone || 'brak telefonu'} · {contact.email || 'brak e-maila'}</small></div>)}
            </div>
          ) : <div className="prospecting-detail-note">Brak zsynchronizowanych osób kontaktowych z Pipedrive.</div>}
          {crm?.sales ? (
            <div className="prospecting-crm-sales-state">
              <div className="prospecting-detail-note">Etykiety CRM: {crm.sales.temperature || 'bez Hot/Warm/Cold'}{crm.sales.preliminary_interest ? ' · wstępne zainteresowanie' : ''}{crm.sales.last_lost_at ? ` · ostatnia przegrana ${String(crm.sales.last_lost_at).slice(0, 10)}` : ''}</div>
              {crm.sales.lead_id ? <Value label="Potencjalny klient (Lead)">{crm.sales.lead_title || crm.sales.lead_id}</Value> : null}
              {crm.sales.lead_value !== null && crm.sales.lead_value !== undefined ? <Value label="Wartość Leada">{`${Number(crm.sales.lead_value).toLocaleString('pl-PL')} ${crm.sales.lead_currency || 'PLN'}`}</Value> : null}
              {crm.sales.deal_id ? <Value label="Szansa sprzedaży">{`${crm.sales.deal_title || `Deal #${crm.sales.deal_id}`} · ${crm.sales.deal_status || 'status nieustalony'}`}</Value> : null}
              {crm.sales.deal_value !== null && crm.sales.deal_value !== undefined ? <Value label="Wartość szansy">{`${Number(crm.sales.deal_value).toLocaleString('pl-PL')} ${crm.sales.deal_currency || 'PLN'}`}</Value> : null}
              {crm.sales.last_lost_reason ? <Value label="Powód ostatniej przegranej">{crm.sales.last_lost_reason}</Value> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}
